import * as THREE from 'three';
import { IntersectionMode } from '../engine/viewer/viewermodel.js';

function GetApiUrl ()
{
    return window.location.pathname.indexOf ('/website/') !== -1 ? '../api/corrections.php' : 'api/corrections.php';
}

function GetShareId ()
{
    let shareId = new URL (window.location.href).searchParams.get ('share');
    if (shareId === null || !/^[a-f0-9]{24}$/.test (shareId)) {
        return null;
    }
    return shareId;
}

function CreateAuthorToken ()
{
    let values = new Uint32Array (4);
    window.crypto.getRandomValues (values);
    return Array.from (values, (value) => value.toString (16).padStart (8, '0')).join ('');
}

function ClearElement (element)
{
    while (element.firstChild !== null) {
        element.firstChild.remove ();
    }
}

function AddElement (parent, type, className, text)
{
    let element = document.createElement (type);
    if (className !== null) {
        element.className = className;
    }
    if (text !== null) {
        element.textContent = text;
    }
    parent.appendChild (element);
    return element;
}

export class CorrectionTool
{
    constructor (viewer, viewerDiv, button, markersDiv, panelDiv)
    {
        this.viewer = viewer;
        this.viewerDiv = viewerDiv;
        this.button = button;
        this.markersDiv = markersDiv;
        this.panelDiv = panelDiv;
        this.shareId = GetShareId ();
        this.authorToken = null;
        this.corrections = [];
        this.markerElements = [];
        this.active = false;
        this.panelVisible = false;
        this.hasModel = false;
        this.draft = null;
        this.animationFrame = null;
        this.statusMessage = '';
    }

    Init ()
    {
        if (this.shareId === null) {
            return;
        }

        let storageKey = 'rionick_correction_author_' + this.shareId;
        this.authorToken = window.localStorage.getItem (storageKey);
        if (this.authorToken === null) {
            this.authorToken = CreateAuthorToken ();
            window.localStorage.setItem (storageKey, this.authorToken);
        }

        this.button.style.display = 'flex';
        this.button.addEventListener ('click', () => {
            this.SetActive (!this.active);
        });
        this.RenderPanel ();
        this.LoadCorrections ();
    }

    IsActive ()
    {
        return this.active;
    }

    SetActive (active)
    {
        this.active = active && this.shareId !== null;
        this.button.classList.toggle ('selected', this.active);
        this.button.setAttribute ('aria-pressed', this.active ? 'true' : 'false');
        this.markersDiv.classList.toggle ('visible', this.active);
        document.body.classList.toggle ('rionick_correction_mode', this.active);

        if (this.active) {
            this.SetPanelVisible (false);
            this.LoadCorrections ();
            this.StartMarkerUpdates ();
        } else {
            this.SetPanelVisible (false);
            this.draft = null;
            this.StopMarkerUpdates ();
            this.RenderPanel ();
            this.RenderMarkers ();
        }
    }

    SetPanelVisible (visible)
    {
        this.panelVisible = visible && this.active;
        this.panelDiv.classList.toggle ('visible', this.panelVisible);
    }

    OnModelLoaded ()
    {
        this.hasModel = true;
        if (this.shareId !== null) {
            this.LoadCorrections ();
        }
    }

    OnModelCleared ()
    {
        this.hasModel = false;
        this.draft = null;
        this.SetPanelVisible (false);
        this.RenderMarkers ();
        this.RenderPanel ();
    }

    Click (mouseCoordinates)
    {
        if (!this.active || !this.hasModel) {
            return;
        }

        let intersection = this.viewer.GetMeshIntersectionUnderMouse (IntersectionMode.MeshOnly, mouseCoordinates);
        if (intersection === null) {
            this.SetPanelVisible (false);
            return;
        }

        let nextNumber = this.corrections.reduce ((maximum, correction) => {
            return Math.max (maximum, correction.number);
        }, 0) + 1;
        this.draft = {
            number : nextNumber,
            position : [intersection.point.x, intersection.point.y, intersection.point.z]
        };
        this.statusMessage = '';
        this.RenderPanel ();
        this.RenderMarkers ();
        this.viewer.FocusPoint (intersection.point, 0.62);
        this.SetPanelVisible (true);

        let textarea = this.panelDiv.querySelector ('textarea');
        if (textarea !== null) {
            textarea.focus ();
        }
    }

    LoadCorrections ()
    {
        if (this.shareId === null) {
            return;
        }

        fetch (GetApiUrl () + '?shareId=' + encodeURIComponent (this.shareId), {
            method : 'GET',
            cache : 'no-store',
            headers : this.GetHeaders ()
        }).then ((response) => response.json ().then ((result) => {
            if (!response.ok || !result.success) {
                throw new Error (result.error || 'Corrections could not be loaded.');
            }
            this.corrections = result.corrections;
            this.statusMessage = '';
            this.RenderPanel ();
            this.RenderMarkers ();
        })).catch ((error) => {
            this.statusMessage = error.message;
            this.RenderPanel ();
        });
    }

    GetHeaders ()
    {
        return {
            'Content-Type' : 'application/json',
            'X-Correction-Token' : this.authorToken
        };
    }

    SendAction (payload)
    {
        payload.shareId = this.shareId;
        return fetch (GetApiUrl (), {
            method : 'POST',
            headers : this.GetHeaders (),
            body : JSON.stringify (payload)
        }).then ((response) => response.json ().then ((result) => {
            if (!response.ok || !result.success) {
                throw new Error (result.error || 'The correction could not be saved.');
            }
            this.corrections = result.corrections;
            this.statusMessage = '';
            this.RenderPanel ();
            this.RenderMarkers ();
        }));
    }

    PostDraft (text)
    {
        let cleanText = text.trim ();
        if (cleanText.length === 0) {
            this.statusMessage = 'Write feedback before posting.';
            this.RenderPanel ();
            return;
        }

        this.SendAction ({
            action : 'create',
            text : cleanText,
            position : this.draft.position
        }).then (() => {
            this.draft = null;
            this.RenderPanel ();
            this.RenderMarkers ();
        }).catch ((error) => {
            this.statusMessage = error.message;
            this.RenderPanel ();
        });
    }

    UpdateCorrection (correction, text)
    {
        let cleanText = text.trim ();
        if (cleanText.length === 0) {
            return;
        }
        this.SendAction ({
            action : 'update',
            correctionId : correction.id,
            text : cleanText
        }).catch ((error) => {
            this.statusMessage = error.message;
            this.RenderPanel ();
        });
    }

    DeleteCorrection (correction)
    {
        if (!window.confirm ('Delete correction #' + correction.number + '?')) {
            return;
        }
        this.SendAction ({
            action : 'delete',
            correctionId : correction.id
        }).catch ((error) => {
            this.statusMessage = error.message;
            this.RenderPanel ();
        });
    }

    RenderPanel ()
    {
        if (this.shareId === null) {
            return;
        }

        ClearElement (this.panelDiv);
        let header = AddElement (this.panelDiv, 'div', 'rionick_correction_panel_header', null);
        let heading = AddElement (header, 'div', null, null);
        AddElement (heading, 'strong', null, 'Corrections');
        AddElement (heading, 'span', null, this.corrections.length + (this.corrections.length === 1 ? ' point' : ' points'));
        let closeButton = AddElement (header, 'button', 'rionick_correction_close', '×');
        closeButton.type = 'button';
        closeButton.setAttribute ('aria-label', 'Hide corrections');
        closeButton.addEventListener ('click', () => this.SetPanelVisible (false));

        if (!this.hasModel) {
            AddElement (this.panelDiv, 'p', 'rionick_correction_hint', 'Waiting for the shared model to load.');
        } else if (this.draft === null) {
            AddElement (this.panelDiv, 'p', 'rionick_correction_hint', 'Click any point on the model to place a numbered correction.');
        }

        if (this.statusMessage.length > 0) {
            AddElement (this.panelDiv, 'p', 'rionick_correction_error', this.statusMessage);
        }

        if (this.draft !== null) {
            let composer = AddElement (this.panelDiv, 'div', 'rionick_correction_composer', null);
            AddElement (composer, 'strong', null, 'New correction #' + this.draft.number);
            let textarea = AddElement (composer, 'textarea', null, null);
            textarea.placeholder = 'Describe what needs to be corrected...';
            textarea.maxLength = 2000;
            let actions = AddElement (composer, 'div', 'rionick_correction_actions', null);
            let cancel = AddElement (actions, 'button', 'secondary', 'Cancel');
            cancel.type = 'button';
            cancel.addEventListener ('click', () => {
                this.draft = null;
                this.RenderPanel ();
                this.RenderMarkers ();
                this.SetPanelVisible (false);
            });
            let post = AddElement (actions, 'button', 'primary', 'Post correction');
            post.type = 'button';
            post.addEventListener ('click', () => this.PostDraft (textarea.value));
        }

        let list = AddElement (this.panelDiv, 'div', 'rionick_correction_list', null);
        for (let correction of this.corrections) {
            this.AddCorrectionCard (list, correction);
        }
        if (this.corrections.length === 0 && this.draft === null && this.hasModel) {
            AddElement (list, 'div', 'rionick_correction_empty', 'No corrections posted yet.');
        }
    }

    AddCorrectionCard (parent, correction)
    {
        let card = AddElement (parent, 'article', 'rionick_correction_card', null);
        card.id = 'correction_' + correction.id;
        let top = AddElement (card, 'div', 'rionick_correction_card_top', null);
        AddElement (top, 'span', 'rionick_correction_number', String (correction.number));
        AddElement (top, 'time', null, new Date (correction.updatedAt).toLocaleString ());
        AddElement (card, 'p', null, correction.text);

        if (correction.canEdit) {
            let actions = AddElement (card, 'div', 'rionick_correction_card_actions', null);
            let edit = AddElement (actions, 'button', null, 'Edit');
            edit.type = 'button';
            edit.addEventListener ('click', () => this.ShowEditor (card, correction));
            let remove = AddElement (actions, 'button', 'danger', 'Delete');
            remove.type = 'button';
            remove.addEventListener ('click', () => this.DeleteCorrection (correction));
        }
    }

    ShowEditor (card, correction)
    {
        ClearElement (card);
        AddElement (card, 'strong', null, 'Edit correction #' + correction.number);
        let textarea = AddElement (card, 'textarea', null, null);
        textarea.value = correction.text;
        textarea.maxLength = 2000;
        let actions = AddElement (card, 'div', 'rionick_correction_actions', null);
        let cancel = AddElement (actions, 'button', 'secondary', 'Cancel');
        cancel.type = 'button';
        cancel.addEventListener ('click', () => this.RenderPanel ());
        let save = AddElement (actions, 'button', 'primary', 'Save');
        save.type = 'button';
        save.addEventListener ('click', () => this.UpdateCorrection (correction, textarea.value));
        textarea.focus ();
    }

    RenderMarkers ()
    {
        ClearElement (this.markersDiv);
        this.markerElements = [];
        if (!this.active) {
            return;
        }

        for (let correction of this.corrections) {
            this.AddMarker (correction, false);
        }
        if (this.draft !== null) {
            this.AddMarker (this.draft, true);
        }
        this.UpdateMarkerPositions ();
    }

    AddMarker (correction, draft)
    {
        let marker = AddElement (this.markersDiv, 'button', 'rionick_correction_marker' + (draft ? ' draft' : ''), String (correction.number));
        marker.type = 'button';
        marker.title = draft ? 'New correction' : correction.text;
        marker.addEventListener ('click', (event) => {
            event.stopPropagation ();
            this.viewer.FocusPoint ({
                x : correction.position[0],
                y : correction.position[1],
                z : correction.position[2]
            }, 0.62);
            this.SetPanelVisible (true);
            let card = document.getElementById ('correction_' + correction.id);
            if (card !== null) {
                card.scrollIntoView ({ behavior : 'smooth', block : 'nearest' });
                card.classList.add ('focused');
                setTimeout (() => card.classList.remove ('focused'), 900);
            }
        });
        this.markerElements.push ({
            element : marker,
            point : new THREE.Vector3 (correction.position[0], correction.position[1], correction.position[2])
        });
    }

    StartMarkerUpdates ()
    {
        if (this.animationFrame !== null) {
            return;
        }
        let update = () => {
            this.UpdateMarkerPositions ();
            this.animationFrame = window.requestAnimationFrame (update);
        };
        update ();
    }

    StopMarkerUpdates ()
    {
        if (this.animationFrame !== null) {
            window.cancelAnimationFrame (this.animationFrame);
            this.animationFrame = null;
        }
    }

    UpdateMarkerPositions ()
    {
        if (!this.active || this.markerElements.length === 0) {
            return;
        }
        let canvas = this.viewer.GetCanvas ();
        let camera = this.viewer.GetThreeCamera ();
        let width = canvas.clientWidth;
        let height = canvas.clientHeight;
        this.markersDiv.style.left = canvas.offsetLeft + 'px';
        this.markersDiv.style.top = canvas.offsetTop + 'px';
        this.markersDiv.style.width = width + 'px';
        this.markersDiv.style.height = height + 'px';

        for (let marker of this.markerElements) {
            let projected = marker.point.clone ().project (camera);
            let visible = projected.z >= -1.0 && projected.z <= 1.0;
            marker.element.style.display = visible ? 'flex' : 'none';
            if (visible) {
                marker.element.style.left = ((projected.x + 1.0) * 0.5 * width) + 'px';
                marker.element.style.top = ((1.0 - projected.y) * 0.5 * height) + 'px';
            }
        }
    }
}
